import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../Button';
import DirectTokenPurchaseModal from '../auth/DirectTokenPurchaseModal';
import styles from '../../styles/test/TestStyles.module.css';
import { TEST_CONFIG } from '../../utils/testConstants';
import freeTrialManager from '../../services/auth/freeTrialManager';

const TestResults = ({ resultado, isFreeTest }) => {
  const isFullTest = resultado.correctas + resultado.incorrectas + resultado.noContestadas === TEST_CONFIG.TOTAL_QUESTIONS;
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [userStatus, setUserStatus] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchUserStatus = async () => {
      try {
        const status = await freeTrialManager.getUserStatus();
        if (mounted) {
          setUserStatus(status);
        }
      } catch (err) {
        console.error('Error fetching user status:', err);
        if (mounted) {
          setUserStatus({ type: 'trial', testsAvailable: 0 });
        }
      }
    };

    fetchUserStatus();

    return () => {
      mounted = false;
    };
  }, []);

  const handleNewTest = () => {
    if (isAuthenticated || !isFreeTest) {
      window.location.reload();
    } else {
      navigate('/', {
        state: {
          message: userStatus?.nextTestTime ? 
            `Has completado tu test gratuito del día. Próximo test disponible en: ${freeTrialManager.formatTimeUntilNextTest(userStatus.nextTestTime)}` :
            'Has completado tu test gratuito del día.'
        }
      });
    }
  };

  const handleReviewMistakes = () => {
    if (isAuthenticated || !isFreeTest) {
      navigate('/repaso');
    } else {
      setShowTokenModal(true);
    }
  };

  return (
    <div className={styles.resultContainer}>
      <DirectTokenPurchaseModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
      />
      <div className={`${styles.resultBox} ${resultado.aprobado ? styles.aprobado : styles.noAprobado}`}>
        <h3>{resultado.aprobado ? '¡APROBADO!' : 'NO APROBADO'}</h3>
        <div className={styles.resultDetails}>
          {isFullTest ? (
            <>
              <h4>Resultados del test oficial ({TEST_CONFIG.TOTAL_QUESTIONS} preguntas):</h4>
              <ul>
                <li>
                  <span>Correctas:</span>
                  <span>{resultado.correctas} × {TEST_CONFIG.SCORING.CORRECT} = {resultado.correctas * TEST_CONFIG.SCORING.CORRECT} puntos</span>
                </li>
                <li>
                  <span>Incorrectas:</span>
                  <span>{resultado.incorrectas} × {TEST_CONFIG.SCORING.INCORRECT} = {(resultado.incorrectas * TEST_CONFIG.SCORING.INCORRECT).toFixed(1)} puntos</span>
                </li>
                <li>
                  <span>En blanco:</span>
                  <span>{resultado.noContestadas} (no puntúan)</span>
                </li>
                <li className={styles.totalPuntos}>
                  <span>Puntuación final:</span>
                  <span>{resultado.puntuacion.toFixed(1)} puntos</span>
                </li>
                <li className={styles.conclusion}>
                  {resultado.puntuacion >= TEST_CONFIG.PASSING_SCORE
                    ? `¡Has superado el test! (mínimo ${TEST_CONFIG.PASSING_SCORE} puntos)`
                    : `No has alcanzado la puntuación mínima de ${TEST_CONFIG.PASSING_SCORE} puntos`}
                </li>
              </ul>
            </>
          ) : (
            <>
              <h4>Resultados del test de práctica ({resultado.correctas + resultado.incorrectas + resultado.noContestadas} preguntas):</h4>
              <ul>
                <li>
                  <span>Correctas:</span>
                  <span>{resultado.correctas} × {TEST_CONFIG.SCORING.CORRECT} = {resultado.correctas} puntos</span>
                </li>
                <li>
                  <span>Incorrectas:</span>
                  <span>{resultado.incorrectas} × {TEST_CONFIG.SCORING.INCORRECT} = {(resultado.incorrectas * TEST_CONFIG.SCORING.INCORRECT).toFixed(1)} puntos</span>
                </li>
                <li>
                  <span>En blanco:</span>
                  <span>{resultado.noContestadas} (no puntúan)</span>
                </li>
                <li className={styles.totalPuntos}>
                  <span>Puntuación actual:</span>
                  <span>{resultado.puntuacion.toFixed(1)} puntos</span>
                </li>
              </ul>

              <h4>Proyección al examen oficial ({TEST_CONFIG.TOTAL_QUESTIONS} preguntas):</h4>
              {(() => {
                const totalPreguntas = resultado.correctas + resultado.incorrectas + resultado.noContestadas;
                const correctasProyectadas = Math.round((resultado.correctas / totalPreguntas) * TEST_CONFIG.TOTAL_QUESTIONS);
                const incorrectasProyectadas = Math.round((resultado.incorrectas / totalPreguntas) * TEST_CONFIG.TOTAL_QUESTIONS);
                const blancoProyectadas = TEST_CONFIG.TOTAL_QUESTIONS - correctasProyectadas - incorrectasProyectadas;
                const puntuacionProyectada = correctasProyectadas + (incorrectasProyectadas * TEST_CONFIG.SCORING.INCORRECT);

                return (
                  <ul>
                    <li>
                      <span>Correctas estimadas:</span>
                      <span>{correctasProyectadas} × {TEST_CONFIG.SCORING.CORRECT} = {correctasProyectadas} puntos</span>
                    </li>
                    <li>
                      <span>Incorrectas estimadas:</span>
                      <span>{incorrectasProyectadas} × {TEST_CONFIG.SCORING.INCORRECT} = {(incorrectasProyectadas * TEST_CONFIG.SCORING.INCORRECT).toFixed(1)} puntos</span>
                    </li>
                    <li>
                      <span>En blanco estimadas:</span>
                      <span>{blancoProyectadas}</span>
                    </li>
                    <li className={styles.totalPuntos}>
                      <span>Puntuación proyectada:</span>
                      <span>{puntuacionProyectada.toFixed(1)} puntos</span>
                    </li>
                    <li className={styles.conclusion}>
                      {puntuacionProyectada >= TEST_CONFIG.PASSING_SCORE
                        ? `Siguiendo esta proporción de respuestas, APROBARÍAS el examen oficial (mínimo ${TEST_CONFIG.PASSING_SCORE} puntos).`
                        : `Siguiendo esta proporción de respuestas, NO APROBARÍAS el examen oficial (mínimo ${TEST_CONFIG.PASSING_SCORE} puntos).`}
                    </li>
                  </ul>
                );
              })()}
            </>
          )}
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={`${styles.statBox} ${styles.correctas}`}>
          <h4>Correctas</h4>
          <p>{resultado.correctas}</p>
        </div>
        <div className={`${styles.statBox} ${styles.incorrectas}`}>
          <h4>Incorrectas</h4>
          <p>{resultado.incorrectas}</p>
        </div>
        <div className={`${styles.statBox} ${styles.noContestadas}`}>
          <h4>En blanco</h4>
          <p>{resultado.noContestadas}</p>
        </div>
      </div>

      {isFreeTest && userStatus?.type === 'trial' && (
        <div className={styles.trialMessage}>
          <p>
            Has completado tu test gratuito del día. 
            {resultado.incorrectas > 0 
              ? ' Para repasar tus fallos y hacer más tests, obtén acceso premium.' 
              : ' Para hacer más tests, obtén acceso premium.'}
          </p>
        </div>
      )}

      <div className={styles.resultButtonsContainer}>
        <div className={styles.actionButtons}>
          <div className={styles.actionButtonsInner}>
            <Button
              variant="primary"
              onClick={() => navigate('/')}
              aria-label="Volver a la página principal"
            >
              Volver al inicio
            </Button>
            
            {isAuthenticated || !isFreeTest ? (
              <Button
                variant="secondary"
                onClick={handleNewTest}
                aria-label="Comenzar un nuevo test"
              >
                Realizar otro test
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => setShowTokenModal(true)}
                aria-label="Obtener acceso premium"
              >
                Obtener acceso premium
              </Button>
            )}

            {(isAuthenticated || !isFreeTest || resultado.incorrectas > 0) && (
              <Button
                variant="success"
                onClick={handleReviewMistakes}
                aria-label="Ir a la página de repaso de fallos"
              >
                Repasar fallos
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResults;